import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiLogOut, FiSettings, FiGrid, FiArchive, FiClipboard, FiHelpCircle, FiCode, FiMenu, FiX, FiHome } = FiIcons;

const navLinks = [
  { to: '/start-building', text: 'Start Building', icon: FiHome },
  { to: '/categories', text: 'Templates', icon: FiGrid },
  { to: '/saved-projects', text: 'Projects', icon: FiArchive },
  { to: '/setup-guides', text: 'Setup', icon: FiHelpCircle },
  { to: '/tool-code-extractor', text: 'Code Extractor', icon: FiCode },
  { to: '/settings', text: 'Settings', icon: FiSettings },
];

const NavItem = ({ to, text, icon, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
        isActive
          ? 'bg-indigo-600 text-white shadow-lg'
          : 'text-gray-600 hover:bg-white hover:text-indigo-600 hover:shadow-md'
      }`
    }
  >
    <SafeIcon icon={icon} className="mr-3 h-5 w-5" />
    {text}
  </NavLink>
);

function Header() {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('promptly_session');
    navigate('/login');
  };

  const toggleMobileMenu = () => setMobileMenuOpen(!isMobileMenuOpen);

  return (
    <header className="bg-white/90 backdrop-blur-xl shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Promptly 2.0
              </h1>
            </div>
          </div>
          
          <nav className="hidden md:flex items-center space-x-2">
            {navLinks.map((link) => (
              <NavItem key={link.to} {...link} />
            ))}
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200 ml-4"
            >
              <SafeIcon icon={FiLogOut} className="mr-2 h-5 w-5" />
              Logout
            </button>
          </nav>
          
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none transition-colors"
            >
              <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-gray-100"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white">
              {navLinks.map((link) => (
                <NavItem key={link.to} {...link} onClick={() => setMobileMenuOpen(false)} />
              ))}
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200"
              >
                <SafeIcon icon={FiLogOut} className="mr-3 h-5 w-5" />
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Header;